import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, onSnapshot, doc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Agent } from '../lib/state';
import { FirestoreWithFallback, FirestoreFallback } from '../lib/firestore-retry';

export const useFirestoreAgents = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setAgents([]);
      setLoading(false);
      return;
    }

    const agentsRef = collection(db, 'users', user.uid, 'agents');
    let unsubscribe: (() => void) | null = null;
    let retryTimeout: NodeJS.Timeout | null = null;
    let retryCount = 0;
    const maxRetries = 3;
    let isMounted = true;
    
    const setupListener = () => {
      if (!isMounted) return;
      
      unsubscribe = onSnapshot(agentsRef, 
        (snapshot) => {
          if (!isMounted) return;
          retryCount = 0; // Reset retry count on success
          const agentsData: Agent[] = [];
          snapshot.forEach((doc) => {
            agentsData.push({
              id: doc.id,
              ...doc.data()
            } as Agent);
          });
          setAgents(agentsData);
          setLoading(false);
          setError(null);
        },
        (err: any) => {
          if (!isMounted) return;
          
          // Handle specific error types gracefully
          const errorCode = err?.code || '';
          const errorMessage = err?.message || '';
          const errorString = JSON.stringify(err).toLowerCase();
          
          // Don't log QUIC/network errors as errors - they're often transient
          const isNetworkError = errorMessage.includes('QUIC') || 
                                 errorMessage.includes('WebChannel') || 
                                 errorMessage.includes('quic_too_many_rtos') ||
                                 errorString.includes('err_quic_protocol_error') ||
                                 errorCode === 'unavailable' ||
                                 errorCode === 'cancelled';
          
          if (isNetworkError && retryCount < maxRetries) {
            console.warn(`Firestore connection issue (retry ${retryCount + 1}/${maxRetries}):`, errorCode);
            
            // Retry connection with exponential backoff
            retryCount++;
            retryTimeout = setTimeout(() => {
              if (isMounted) {
                setupListener();
              }
            }, 1000 * Math.pow(2, retryCount - 1)); // Exponential backoff: 1s, 2s, 4s
            return;
          }
          
          // Only log actual errors (not network/transient issues)
          if (errorCode !== 'permission-denied' && errorCode !== 'unavailable' && errorCode !== 'cancelled' && !isNetworkError) {
            console.error('Error loading agents:', err);
          } else if (retryCount >= maxRetries) {
            console.warn('Max retries reached for Firestore connection. Using cached data if available.');
          }
          
          setError(isNetworkError && retryCount < maxRetries ? null : errorMessage);
          setLoading(false);
        }
      );
    };

    setupListener();
    
    return () => {
      isMounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (unsubscribe) unsubscribe();
    };
  }, [user]);

  const addAgent = async (agent: Omit<Agent, 'id'>) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      // Clean and validate agent data
      const cleanAgent = {
        name: agent.name || '',
        description: agent.description || '',
        status: agent.status || 'inactive',
        language: agent.language || 'English',
        industry: agent.industry || 'General',
        voice: agent.voice || 'Zephyr',
        allowInterruptions: Boolean(agent.allowInterruptions),
        noiseReduction: Boolean(agent.noiseReduction),
        systemPrompt: agent.systemPrompt || 'You are a helpful AI assistant.',
        formality: Number(agent.formality) || 50,
        enthusiasm: Number(agent.enthusiasm) || 50,
        knowledgeBase: Array.isArray(agent.knowledgeBase) ? agent.knowledgeBase : [],
        model: agent.model || 'gemini-2.5-flash',
        temperature: Number(agent.temperature) || 0.7,
        topP: Number(agent.topP) || 1,
        tools: Array.isArray(agent.tools) ? agent.tools : [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('Adding agent with data:', cleanAgent);
      
      const agentsRef = collection(db, 'users', user.uid, 'agents');
      const docRef = await addDoc(agentsRef, cleanAgent);
      console.log('Agent added successfully with ID:', docRef.id);
    } catch (error) {
      console.error('Error adding agent:', error);
      throw error;
    }
  };

  const updateAgent = async (agentId: string, updates: Partial<Agent>) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const agentRef = doc(db, 'users', user.uid, 'agents', agentId);
      
      // Clean and validate update data
      const cleanUpdates = {
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      console.log('Updating agent with data:', cleanUpdates);
      
      await updateDoc(agentRef, cleanUpdates);
      console.log('Agent updated successfully');
    } catch (error) {
      console.error('Error updating agent:', error);
      throw error;
    }
  };

  const deleteAgent = async (agentId: string) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const agentRef = doc(db, 'users', user.uid, 'agents', agentId);
      
      console.log('Deleting agent with ID:', agentId);
      
      await deleteDoc(agentRef);
      console.log('Agent deleted successfully');
    } catch (error) {
      console.error('Error deleting agent:', error);
      throw error;
    }
  };

  return {
    agents,
    loading,
    error,
    addAgent,
    updateAgent,
    deleteAgent
  };
};
